"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Flame, Sparkles, SunMedium, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Pilgrimage Compression & Event Surges",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Capitalize on massive demand spikes during Mahashivratri, Shravan Maas, and Somvati Amavasya while preventing distress rates during mid-week lulls. Dynamic minimum-stay rules and tiered advance pricing protect your yields.",
  metricLabel: "Strategy focus",
  metricValue: "Tiered event minimum-stay rules & demand-driven yield fences"
}, {
  id: 2,
  title: "Aggressive Comp-Set Rate Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Monitor high-frequency rate fluctuations across MakeMyTrip, Booking.com, and Agoda. Real-time competitive alerts and automated rule-based repricing ensure your rooms capture high-intent pilgrims before competitors undercut.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA tracking & dynamic competitor positioning"
}, {
  id: 3,
  title: "OTA Dependency & Offline Walk-in Leakage",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Balance high OTA commissions against unorganized local travel agent and walk-in rates. We enforce strict price parity across all distribution channels while driving direct WhatsApp and website bookings with tailored pilgrim packages.",
  metricLabel: "Strategy focus",
  metricValue: "Channel parity governance & direct pilgrim acquisition"
}];
const seasonalitySeasons = [{
  iconName: "Flame",
  period: "Peak Pilgrimage & Festivals",
  timing: "Shravan (Jul-Aug) | Mahashivratri (Feb-Mar) | Kartik Purnima",
  character: "Demand surges up to 400% above baseline. Rooms sell out weeks ahead.",
  strategy: "Strict 2-3 night minimum stay, non-refundable rates, premium festival surcharge fencing."
}, {
  iconName: "Sparkles",
  period: "Weekend Influx & Auspicious Dates",
  timing: "Friday - Sunday | Somvati Amavasya | Bhasma Aarti Peak Days",
  character: "Compressed booking window from Indore, Bhopal, and transit corridor travelers.",
  strategy: "Intraday dynamic yield shifts, last-room value protection, transit corridor packages."
}, {
  iconName: "SunMedium",
  period: "Mid-Week & Summer Shoulder",
  timing: "Tuesday - Thursday | April to June (Pre-Monsoon)",
  character: "Lower transient footfall, higher price sensitivity across budget and boutique segments.",
  strategy: "Corporate transit rate contracts, extended stay incentives, value-add temple tour bundles."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapeujjain" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Ujjain
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Ujjain is one of India’s most dynamic spiritual hospitality hubs, characterized by extreme pilgrimage compression, sudden weekend spikes from regional metro corridors, and high seasonal volatility. Sunil Singh structures disciplined revenue models engineered specifically for hotels, boutique guest stays, and spiritual retreats across the Mahakal corridor.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-secondary" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-secondary" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-secondary" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <div className="mt-14 md:mt-20 rounded-xl border border-border bg-card p-6 sm:p-8 md:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 pb-6 border-b border-border/70">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-secondary mb-2">
                <Clock className="size-4 text-secondary" />
                <span>Ujjain Market Seasonality Matrix</span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                Calibrating Rates to Spiritual & Tourist Calendars
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Revenue in Ujjain is heavily tied to religious observances and regional travel patterns. We calibrate yield controls across three distinct demand phases:
              </p>
            </div>
            <Badge variant="outline" className="self-start md:self-auto border-secondary/30 text-xs px-3 py-1 font-mono">
              Annualized Yield Calendar
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalitySeasons.map((season, idx) => <div key={season.period} data-index={idx} className="flex flex-col justify-between rounded-lg border border-border/70 bg-background/50 p-5">
                <div>
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="inline-flex size-8 items-center justify-center rounded-md bg-secondary/10 text-secondary">
                      {season.iconName === "Flame" && <Flame className="size-4" />}
                      {season.iconName === "Sparkles" && <Sparkles className="size-4" />}
                      {season.iconName === "SunMedium" && <SunMedium className="size-4" />}
                    </div>
                    <h4 className="font-serif font-bold text-base text-foreground">{season.period}</h4>
                  </div>
                  <p className="text-xs font-medium text-secondary mb-3 leading-tight">{season.timing}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">{season.character}</p>
                </div>
                <div className="pt-3 border-t border-border/50">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground block mb-1">
                    Revenue playbook
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed">{season.strategy}</p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Ujjain hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}